import { Box, Typography, Link } from '@mui/material';
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ViewOnlyContext, useEventDetails } from '../context';
import { buildWelcomeDocViewUrl } from '../utils/welcomeDocUrl';
import WelcomeEmptyState from './WelcomeEmptyState';
import LoadingIndicator from './LoadingIndicator';
import { usePermissions } from '../hooks/usePermissions';
import { useEventHasMap } from '../hooks/useEventHasMap';
import { getDefaultEventTabWhenNoMap } from '../lib/eventDefaultTab';
import { db } from '../instant';
import WelcomeDocHtmlPanel from './WelcomeDocHtmlPanel';

interface Props {
    eventId: string;
}

export default function EventInfo(props: Props) {
    const { eventId } = props;
    const navigate = useNavigate();
    const viewOnly = useContext(ViewOnlyContext);
    const { canEditEvents, canViewWasteData, canViewInventory, canEditUsers, isLoading: permissionsLoading } = usePermissions();
    const { hasMap, isLoading: mapsLoading } = useEventHasMap(eventId);
    const { user, isLoading: authLoading } = db.useAuth();
    const { event, isLoading, error } = useEventDetails(eventId);

    const docId = event?.welcomeDocId ?? null;
    const tabId = event?.welcomeTabId ?? null;
    const welcomeRef = { docId, tabId };
    const viewUrl = buildWelcomeDocViewUrl(welcomeRef);

    useEffect(() => {
        if (!isLoading && event != null && docId == null && !canEditEvents) {
            if (mapsLoading) return;
            const target = hasMap
                ? 'map'
                : getDefaultEventTabWhenNoMap({
                      canViewWasteData,
                      canViewInventory,
                      canEditUsers,
                      canEditEvents,
                  });
            navigate(`/event/${eventId}/${target}`, { replace: true });
        }
    }, [
        isLoading,
        event,
        docId,
        canEditEvents,
        eventId,
        navigate,
        mapsLoading,
        permissionsLoading,
        hasMap,
        canViewWasteData,
        canViewInventory,
        canEditUsers,
    ]);

    if (isLoading) {
        return <LoadingIndicator message="Loading..." />;
    }

    if (error) {
        return (
            <Typography color="error" sx={{ p: 2 }}>
                Error loading event: {error.message}
            </Typography>
        );
    }

    if (!event) {
        return null;
    }

    if (docId == null && !canEditEvents) {
        return null;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                width: '100%',
                padding: 2,
                boxSizing: 'border-box',
                minHeight: 0,
            }}
        >
            {viewUrl && (
                <Box sx={{ marginBottom: 2, textAlign: 'center' }}>
                    <Link href={viewUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.875rem' }}>
                        Open in Google Docs
                    </Link>
                </Box>
            )}
            <Box
                sx={{
                    flex: 1,
                    width: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {docId ? (
                    <WelcomeDocHtmlPanel
                        docId={docId}
                        tabId={tabId}
                        token={user?.refresh_token}
                        authReady={!authLoading}
                        viewOnly={viewOnly}
                        viewUrl={viewUrl}
                    />
                ) : (
                    <WelcomeEmptyState
                        title="No info document set"
                        hint="Editors can set the event info document in the Properties tab."
                        editLink={{ to: `/event/${eventId}/settings`, label: 'Go to Properties tab' }}
                    />
                )}
            </Box>
        </Box>
    );
}
